import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';
import { useQuery } from '@tanstack/react-query';
import { useDashboardStats } from '../hooks/useDashboardStats';
import { PaginaKop, TellerChip } from './ZijMenu';
import api from '../services/api';
import { uploadUrl } from '../utils/uploadUrl';

interface EmployerCockpitHeaderProps {
  /** When provided (cockpit page), use buttons that switch view instead of navigating */
  onReactieClick?: () => void;
  onReviewsClick?: () => void;
  pageTitle?: string;
  pageSubtitle?: string;
  /** Signaaltegels (reacties/reviews/berichten); in de cockpit-layout uit — signalen staan op het Dashboard */
  toonSignalen?: boolean;
}

export const EmployerCockpitHeader: React.FC<EmployerCockpitHeaderProps> = ({
  onReactieClick,
  onReviewsClick,
  pageTitle,
  pageSubtitle,
  toonSignalen = true,
}) => {
  const { t } = useTranslation('common');
  const { user } = useAuthStore();
  const { data: dashboardStats, isLoading: statsLoading } = useDashboardStats();

  const { data: profileData } = useQuery({
    queryKey: ['employer-profile'],
    queryFn: async () => {
      const response = await api.get('/employer/profile');
      return response.data;
    },
    enabled: !!user && (user.role === 'opdrachtgever' || user.role === 'employer_member'),
  });

  const rawLogo = profileData?.data?.logo_url;
  const logoUrl = rawLogo
    ? uploadUrl(rawLogo)
    : null;

  const isCockpitMode = onReactieClick !== undefined && onReviewsClick !== undefined;

  const reactieHighlight = (dashboardStats?.reactie_professionals ?? 0) > 0;
  const reactieValue = statsLoading ? '…' : dashboardStats?.reactie_professionals ?? 0;
  const reviewsValue = statsLoading ? '…' : dashboardStats?.total_reviews ?? 0;
  const berichtenValue = statsLoading ? '…' : dashboardStats?.unread_messages ?? 0;

  // Lichte paginakop in BeLagom-huisstijl (okt 2026); staat in de inhoudskolom naast het ZijMenu
  return (
    <PaginaKop
      titel={pageTitle || t('employerHeader.welcome', { name: user?.first_name })}
      ondertitel={pageSubtitle || undefined}
      acties={toonSignalen ? (
        <>
          {logoUrl && <img src={logoUrl} alt={t('employerHeader.logoAlt', 'Bedrijfslogo')} className="h-9 w-auto max-w-[120px] object-contain mr-1" />}
          <TellerChip
            {...(isCockpitMode ? { onClick: onReactieClick } : { href: '/employer/connections?tab=opdrachten' })}
            aantal={reactieValue}
            label={t('employerHeader.reactieProfessionals', 'Reactie professioneels')}
            nadruk={reactieHighlight}
          />
          <TellerChip
            {...(isCockpitMode ? { onClick: onReviewsClick } : { href: '/employer/cockpit?view=reviews' })}
            aantal={reviewsValue}
            label={t('employerHeader.reviews', 'Reviews')}
            toon="navy"
          />
          <TellerChip href="/berichten" aantal={berichtenValue} label={t('employerHeader.berichten', 'Berichten')} toon="swirl" />
        </>
      ) : undefined}
    />
  );
};
